import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestedName: string;
  onConfirm: (name: string) => Promise<void>;
}

export default function SaveTemplateModal({ open, onOpenChange, suggestedName, onConfirm }: Props) {
  const [name, setName] = useState(suggestedName);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setName(suggestedName);
  }, [open, suggestedName]);

  const handleOpen = (v: boolean) => {
    if (v) setName(suggestedName);
    onOpenChange(v);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onConfirm(name.trim());
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-sm">Save as Template</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label className="text-[11px] font-medium text-muted-foreground">Template Name</Label>
          <Input
            className="h-9 text-xs"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Dallas → Chicago Van"
            maxLength={100}
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
          />
          <p className="text-[10px] text-muted-foreground">This saves the lane & equipment config for quick reuse.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" className="text-xs" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" className="text-xs" onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Saving…</> : "Save Template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
